import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../api";
import AdminSelect from "./AdminSelect";
import timeAgo from "../utils/timeAgo";

/**
 * Bulk-import members from a CSV file.
 *
 * Three-stage flow: upload → map columns → watch progress. The upload
 * creates a `preview` job on the backend; nothing actually writes to
 * the member cache until the admin confirms the mapping and clicks
 * "Start import". Processing happens in a background worker, so the
 * admin can close the tab and come back — progress picks up where it
 * left off, and a "Recent imports" list at the bottom surfaces any
 * older jobs for this venue.
 *
 * Re-importing the same file is safe: the backend upserts on
 * (venue_id, membernumber), so members get updated in place rather
 * than duplicated.
 */

const FIELD_GROUPS = [
  { label: "Identity",         keys: ["membernumber", "firstname", "surname", "title"] },
  { label: "Contact",          keys: ["email", "mobile", "telephone"] },
  { label: "Personal details", keys: ["gender", "dateofbirth", "occupation"] },
  { label: "Address",          keys: ["addressline1", "addressline2", "addressline3", "addressline4", "postcode"] },
  // Membership dates differ by provider: IGT exposes dateofjoining; S7000
  // exposes dateregistered + dateexpiry. Only the active provider's fields
  // actually render (the panel filters to the targets the backend returned).
  { label: "Membership",       keys: ["membershipstatus", "suspensionflag", "dateofjoining", "dateregistered", "dateexpiry"] },
];

export default function CsvMemberImportPanel({ venueId, providerType }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // Preview state (post-upload, pre-start)
  const [preview, setPreview] = useState(null);
  // { jobId, headers, sample, autoMapping, targets }
  const [mapping, setMapping] = useState({});
  const [conflictStrategy, setConflictStrategy] = useState("preserve_blanks");
  const [hasHeader, setHasHeader] = useState(true);
  const [starting, setStarting] = useState(false);

  // Active job state (post-start)
  const [activeJob, setActiveJob] = useState(null);

  // Recent imports list
  const [recent, setRecent] = useState([]);

  const loadRecent = async () => {
    try { setRecent(await adminApi.memberImports.list(venueId)); }
    catch { /* silent */ }
  };

  useEffect(() => { loadRecent(); /* eslint-disable-next-line */ }, [venueId]);

  // Poll the active job until it's done or failed.
  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === "done" || activeJob.status === "failed" || activeJob.status === "cancelled") {
      loadRecent();
      return;
    }
    const intervalMs = activeJob.status === "running" ? 2000 : 3000;
    const t = setTimeout(async () => {
      try {
        const next = await adminApi.memberImports.status(venueId, activeJob.id);
        setActiveJob(next);
      } catch (err) { setError(err.message); }
    }, intervalMs);
    return () => clearTimeout(t);
  }, [venueId, activeJob]);

  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true); setPreview(null); setActiveJob(null);
    try {
      const data = await adminApi.memberImports.upload(venueId, file);
      setPreview(data);
      setMapping(data.autoMapping || {});
    } catch (err) { setError(err.message); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function cancelPreview() {
    if (!preview) return;
    try { await adminApi.memberImports.cancel(venueId, preview.jobId); } catch {}
    setPreview(null); setMapping({}); loadRecent();
  }

  async function startImport() {
    if (!preview) return;
    if (!mapping.membernumber) { setError("Map the Member number column before starting."); return; }
    setStarting(true); setError("");
    try {
      await adminApi.memberImports.start(venueId, preview.jobId, mapping, {
        conflict_strategy: conflictStrategy,
        has_header: hasHeader,
      });
      // Seed the active-job view; polling takes over from here.
      const initial = await adminApi.memberImports.status(venueId, preview.jobId);
      setActiveJob(initial);
      setPreview(null);
    } catch (err) { setError(err.message); }
    setStarting(false);
  }

  async function resumeJob(jobId) {
    setError("");
    try {
      const j = await adminApi.memberImports.status(venueId, jobId);
      // Preview-status jobs return headers/sample/targets so we can drop
      // straight back into the mapping step. Running/queued/done jobs go
      // into the JobProgress view instead.
      if (j.status === "preview" && j.headers && j.targets) {
        setActiveJob(null);
        setPreview({
          jobId: j.id,
          headers: j.headers,
          sample: j.sample || [],
          autoMapping: j.autoMapping || {},
          targets: j.targets,
          providerType: j.providerType,
          filename: j.filename,
        });
        setMapping(j.mapping || j.autoMapping || {});
        if (j.options) {
          if (j.options.conflict_strategy) setConflictStrategy(j.options.conflict_strategy);
          if (typeof j.options.has_header === "boolean") setHasHeader(j.options.has_header);
        }
      } else {
        setPreview(null);
        setActiveJob(j);
      }
    } catch (err) { setError(err.message); }
  }

  async function downloadErrors(jobId) {
    try { await adminApi.memberImports.downloadErrors(venueId, jobId); }
    catch (err) { setError(err.message); }
  }

  return (
    <div className="csv-import-wrap">
      <div className="csv-import-intro">
        <div>
          <h3 className="csv-import-title">Bulk import members</h3>
          <p className="csv-import-desc">
            Upload a CSV or Excel (.xlsx) file, map columns to member fields, then let the import
            run in the background. Existing members get updated in place (no duplicates). You can
            close this page — the import keeps running, and progress picks up when you come back.
          </p>
        </div>
      </div>

      {providerType === "s7000" && (
        <div style={{
          display: "flex", gap: "0.55rem", alignItems: "flex-start",
          padding: "0.7rem 0.85rem", marginBottom: "0.85rem", borderRadius: 8,
          background: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.55)",
          fontSize: "0.85rem", lineHeight: 1.45,
        }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div>
            <strong>S7000 members sync from the gaming system.</strong> This cache is fully refreshed
            from S7000 on a schedule (about every 6 hours) and on any manual “Sync now”. Imported
            members that don’t exist in S7000 will be removed at the next sync — so use import to
            correct or enrich members that already exist in S7000, not to add new ones.
          </div>
        </div>
      )}

      {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

      {/* ── Pending preview banner — surfaces unfinished mappings up top ── */}
      {!preview && !activeJob && recent.filter((j) => j.status === "preview").map((j) => (
        <div key={j.id} className="csv-import-pending">
          <div className="csv-import-pending-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="csv-import-pending-body">
            <div className="csv-import-pending-title">Unfinished import — finish mapping to start</div>
            <div className="csv-import-pending-meta">
              <strong>{j.filename || `Job #${j.id}`}</strong>
              {j.created_at && <> · uploaded {timeAgo(j.created_at)}</>}
            </div>
          </div>
          <div className="csv-import-pending-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => resumeJob(j.id)}>Resume</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={async () => {
              try { await adminApi.memberImports.cancel(venueId, j.id); loadRecent(); }
              catch (err) { setError(err.message); }
            }}>Discard</button>
          </div>
        </div>
      ))}

      {/* ── Upload (shown when there's no preview or active job) ── */}
      {!preview && !activeJob && (
        <div className="csv-import-upload">
          <label className="csv-import-filebtn">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFilePicked}
              disabled={uploading}
            />
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>{uploading ? "Uploading…" : "Choose CSV or Excel file"}</span>
          </label>
          <span className="csv-import-hint">CSV up to 100 MB or Excel .xlsx up to 20 MB · any column headers — map them in the next step.</span>
        </div>
      )}

      {/* ── Mapping + preview (shown after upload, before start) ── */}
      {preview && !activeJob && (
        <MappingStep
          preview={preview}
          mapping={mapping}
          setMapping={setMapping}
          hasHeader={hasHeader}
          setHasHeader={setHasHeader}
          conflictStrategy={conflictStrategy}
          setConflictStrategy={setConflictStrategy}
          onCancel={cancelPreview}
          onStart={startImport}
          starting={starting}
        />
      )}

      {/* ── Active job progress ────────────────────────────────── */}
      {activeJob && <JobProgress job={activeJob} onDismiss={() => { setActiveJob(null); loadRecent(); }} onDownloadErrors={() => downloadErrors(activeJob.id)} />}

      {/* ── Recent imports ─────────────────────────────────────── */}
      {recent.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          <div className="form-section-label">Recent imports</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>New</th>
                  <th>Updated</th>
                  <th>Skipped</th>
                  <th>Errors</th>
                  <th>When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((j) => (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 500 }}>{j.filename || `Job #${j.id}`}</td>
                    <td><StatusBadge status={j.status} /></td>
                    <td>{j.total_rows ?? "—"}</td>
                    <td style={{ color: "var(--success)" }}>{j.new_count ?? 0}</td>
                    <td>{j.updated_count ?? 0}</td>
                    <td style={{ color: "var(--text-muted)" }}>{j.skipped_count ?? 0}</td>
                    <td style={{ color: (j.error_count > 0) ? "var(--danger)" : "var(--text-muted)" }}>{j.error_count ?? 0}</td>
                    <td style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                      {j.created_at ? <>{new Date(j.created_at).toLocaleString()} <span style={{ opacity: 0.7 }}>({timeAgo(j.created_at)})</span></> : "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", gap: "0.35rem" }}>
                        {j.status === "preview" && (
                          <>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => resumeJob(j.id)}>Resume</button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={async () => {
                              try { await adminApi.memberImports.cancel(venueId, j.id); loadRecent(); }
                              catch (err) { setError(err.message); }
                            }}>Discard</button>
                          </>
                        )}
                        {(j.status === "running" || j.status === "queued") && (
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => resumeJob(j.id)}>View</button>
                        )}
                        {(j.error_count > 0) && (j.status === "done" || j.status === "failed") && (
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => downloadErrors(j.id)}>Errors CSV</button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mapping step ─────────────────────────────────────────── */
function MappingStep({ preview, mapping, setMapping, hasHeader, setHasHeader, conflictStrategy, setConflictStrategy, onCancel, onStart, starting }) {
  const { headers, sample, targets } = preview;
  const missingRequired = targets.filter((t) => t.required && !mapping[t.key]);
  const targetsByKey = Object.fromEntries(targets.map((t) => [t.key, t]));

  // Sample value for each header (first non-empty cell across sample rows).
  const sampleByHeader = {};
  headers.forEach((h, idx) => {
    for (const row of sample) {
      const v = row?.[idx];
      if (v !== "" && v != null) { sampleByHeader[h] = String(v); break; }
    }
  });

  // Shape options for AdminSelect; include a hint with the first sample value.
  const dropdownOptions = [
    { value: "", label: "— ignore —" },
    ...headers.map((h) => ({
      value: h,
      label: h,
      hint: sampleByHeader[h] ? sampleByHeader[h].slice(0, 24) : undefined,
    })),
  ];

  // Group targets using FIELD_GROUPS. Unrecognised keys fall into "Other".
  const usedKeys = new Set();
  const groups = FIELD_GROUPS.map((g) => {
    const groupTargets = g.keys.map((k) => targetsByKey[k]).filter(Boolean);
    groupTargets.forEach((t) => usedKeys.add(t.key));
    return { label: g.label, targets: groupTargets };
  }).filter((g) => g.targets.length > 0);
  const extras = targets.filter((t) => !usedKeys.has(t.key));
  if (extras.length > 0) groups.push({ label: "Other", targets: extras });

  const mappedCount = Object.values(mapping).filter(Boolean).length;

  return (
    <div className="csv-import-panel">
      <div className="csv-import-panel-header">
        <div>
          <div className="csv-import-step">Step 2 of 2</div>
          <h4 className="csv-import-panel-title">Map your columns</h4>
          <div className="csv-import-panel-sub">
            Auto-guessed from your headers — adjust as needed. Columns left on <em>— ignore —</em> won't be imported.
            <span className="csv-import-count"> · {mappedCount} of {targets.length} fields mapped</span>
          </div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>

      <div className="csv-import-groups">
        {groups.map((g) => {
          const groupMapped = g.targets.filter((t) => mapping[t.key]).length;
          return (
            <div key={g.label} className="csv-import-group">
              <div className="csv-import-group-title">
                <span>{g.label}</span>
                <span className="csv-import-group-count">{groupMapped}/{g.targets.length}</span>
              </div>
              <div className="csv-import-fields">
                {g.targets.map((t) => {
                  const isMapped = !!mapping[t.key];
                  const isMissing = t.required && !isMapped;
                  return (
                    <div
                      key={t.key}
                      className={`csv-import-field${isMapped ? " mapped" : ""}${isMissing ? " missing" : ""}`}
                    >
                      <label>
                        <span className="csv-import-field-status" aria-hidden>
                          {isMapped ? (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : isMissing ? (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <circle cx="12" cy="12" r="6" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="5" />
                            </svg>
                          )}
                        </span>
                        <span className="csv-import-field-name">{t.label}</span>
                        {t.required && <span className="csv-import-required">*</span>}
                        {t.type === "date" && <span className="csv-import-typehint">date</span>}
                      </label>
                      <AdminSelect
                        value={mapping[t.key] || ""}
                        onChange={(v) => setMapping({ ...mapping, [t.key]: v || undefined })}
                        options={dropdownOptions}
                        placeholder="— ignore —"
                        searchable={headers.length > 8}
                      />
                      {isMapped && sampleByHeader[mapping[t.key]] && (
                        <span className="csv-import-sample" title={sampleByHeader[mapping[t.key]]}>
                          e.g. {sampleByHeader[mapping[t.key]]}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <details className="csv-import-preview">
        <summary>Preview — first {sample.length} rows with your current mapping</summary>
        <div className="table-wrap" style={{ marginTop: "0.5rem", maxHeight: 300, overflow: "auto" }}>
          <table className="data-table" style={{ fontSize: "0.8rem" }}>
            <thead>
              <tr>{targets.filter((t) => mapping[t.key]).map((t) => <th key={t.key}>{t.label}</th>)}</tr>
            </thead>
            <tbody>
              {sample.map((row, i) => (
                <tr key={i}>
                  {targets.filter((t) => mapping[t.key]).map((t) => {
                    const idx = headers.indexOf(mapping[t.key]);
                    return <td key={t.key} style={{ color: "var(--text-muted)" }}>{idx === -1 ? "" : row[idx]}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <div className="csv-import-options">
        <label className="csv-import-check">
          <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
          <span>First row is a header</span>
        </label>
        <div className="csv-import-conflicts">
          <span className="csv-import-conflicts-label">On conflict:</span>
          <div style={{ flex: 1, minWidth: 280 }}>
            <AdminSelect
              value={conflictStrategy}
              onChange={setConflictStrategy}
              options={[
                { value: "preserve_blanks", label: "Keep existing values for blank cells" },
                { value: "overwrite", label: "Overwrite — blank cells wipe existing values" },
              ]}
            />
          </div>
        </div>
      </div>

      {missingRequired.length > 0 && (
        <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>
          Map required column{missingRequired.length > 1 ? "s" : ""}: {missingRequired.map((t) => t.label).join(", ")}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button type="button" className="btn btn-primary" onClick={onStart} disabled={starting || missingRequired.length > 0}>
          {starting ? "Starting…" : `Start import${mappedCount > 0 ? ` (${mappedCount} fields)` : ""}`}
        </button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Discard</button>
      </div>
    </div>
  );
}

/* ── Active-job progress ─────────────────────────────────── */
function JobProgress({ job, onDismiss, onDownloadErrors }) {
  const pct = job.total_rows > 0 ? Math.round((job.processed / job.total_rows) * 100) : 0;
  const terminal = job.status === "done" || job.status === "failed" || job.status === "cancelled";

  return (
    <div className="csv-import-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div>
          <strong>{job.filename || `Job #${job.id}`}</strong>
          <StatusBadge status={job.status} style={{ marginLeft: "0.5rem" }} />
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={onDismiss}>
          {terminal ? "Close" : "Hide (keeps running)"}
        </button>
      </div>

      {!terminal && (
        <div style={{ marginBottom: "0.5rem" }}>
          <div style={{ background: "var(--border)", borderRadius: 4, height: 10, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, background: "var(--accent)", height: "100%", transition: "width 0.3s" }} />
          </div>
          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
            {job.processed?.toLocaleString() || 0} / {job.total_rows?.toLocaleString() || "?"} rows ({pct}%)
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", fontSize: "0.85rem", marginTop: "0.5rem" }}>
        <span>New: <strong style={{ color: "var(--success)" }}>{job.new_count ?? 0}</strong></span>
        <span>Updated: <strong>{job.updated_count ?? 0}</strong></span>
        <span>Skipped: <strong style={{ color: "var(--text-muted)" }}>{job.skipped_count ?? 0}</strong></span>
        <span>Errors: <strong style={{ color: (job.error_count > 0) ? "var(--danger)" : "var(--text-muted)" }}>{job.error_count ?? 0}</strong></span>
      </div>

      {job.error && (
        <div className="toast toast-error" style={{ marginTop: "0.75rem" }}>⚠ {job.error}</div>
      )}

      {terminal && (job.error_count > 0) && (
        <div style={{ marginTop: "0.75rem" }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={onDownloadErrors}>
            Download errors CSV
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Status badge ─────────────────────────────────────────── */
function StatusBadge({ status, style }) {
  const color = status === "done"       ? "var(--success)"
              : status === "failed"     ? "var(--danger)"
              : status === "cancelled"  ? "var(--text-muted)"
              : status === "running"    ? "var(--warning)"
              : "var(--text-muted)";
  return (
    <span style={{
      display: "inline-block", padding: "0.1rem 0.5rem", borderRadius: 999,
      fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
      background: "transparent", color, border: `1px solid ${color}`,
      ...style,
    }}>
      {status}
    </span>
  );
}
